import { memo } from 'react'

import * as MdIcons from 'react-icons/md'
import { useParams, useNavigate } from 'react-router-dom'
import { isAfter, isWithinInterval } from 'date-fns'
import { useTheme } from 'styled-components'

import { Tile } from '@components/Tile'
import { useSharedDates } from '@contexts/sharedDates'
import { useDate } from '@hooks/dates'
import { IGenericTask } from '@interfaces/task/generic/IGenericTask'

interface TaskProps extends IGenericTask {
  operationPost?: { description: string }
}

interface IDownstreamTile {
  task: TaskProps
}

export const DownstreamTile = memo(function DownstreamTile({
  task,
}: IDownstreamTile) {
  const theme = useTheme()

  const navigate = useNavigate()

  const { getWeekRange } = useDate()

  const { selectedDate } = useSharedDates()

  const { weekStart: currentWeekStart, weekEnd: currentWeekEnd } = getWeekRange(
    new Date(),
  )

  const isSelectedDateBetweenRange = isWithinInterval(selectedDate, {
    start: currentWeekStart,
    end: currentWeekEnd,
  })
  const isSelectedDateAhead = isAfter(selectedDate, currentWeekEnd)

  function handleTilePress(taskId: number) {
    navigate(`/task/${taskId}`)
  }

  const tileBackground = isSelectedDateBetweenRange
    ? theme.colors.tileForeground
    : isSelectedDateAhead
      ? theme.colors.tileAheadCurrentWeek
      : theme.colors.tileBehindCurrentWeek

  return (
    <Tile.Provider isSelectEnabled={true} isSelectable={false}>
      <Tile.Root
        tileBackground={tileBackground}
        onPress={() => handleTilePress(task.id)}
        onSelection={() => {}}
      >
        <Tile.StatusPill status={task.status} />
        <Tile.Checkbox />
        <Tile.Container>
          <Tile.Header>
            <Tile.Title>{task.name}</Tile.Title>
          </Tile.Header>

          <Tile.Body>
            {!!task.code ? (
              <Tile.Value>{task.code}</Tile.Value>
            ) : (
              <Tile.Value>DWS</Tile.Value>
            )}
            <Tile.Row>
              <Tile.Label>Operation Post</Tile.Label>
              <Tile.Value>{task.operationPost?.description}</Tile.Value>
            </Tile.Row>
            <Tile.Row>
              <Tile.Label>Equipment</Tile.Label>
              <Tile.Value>{task.equipmentTag}</Tile.Value>
            </Tile.Row>
          </Tile.Body>

          <Tile.Footer.Container>
            <Tile.Footer.Box>
              <MdIcons.MdPeopleOutline
                size={24}
                color={theme.colors.info}
              />
              <Tile.Label>{task.crewSize}</Tile.Label>
            </Tile.Footer.Box>
            <Tile.Footer.Box>
              <MdIcons.MdAccessTime
                size={24}
                color={theme.colors.info}
              />
              <Tile.Label>{task.duration}</Tile.Label>
            </Tile.Footer.Box>
            <Tile.Footer.Box>
              <MdIcons.MdFormatListNumbered
                size={24}
                color={theme.colors.info}
              />
              <Tile.Label>{`${task.sequence + 1} / ${task.jobTaskCount}`}</Tile.Label>
            </Tile.Footer.Box>
            {task.jobAbcIndicator && (
              <Tile.Footer.Box>
                <MdIcons.MdAbc size={24} color={theme.colors.info} />
                <Tile.Label>{task.jobAbcIndicator}</Tile.Label>
              </Tile.Footer.Box>
            )}
            {task.hasBarrier && (
              <MdIcons.MdFence
                size={24}
                color={theme.colors.hasBarrier}
              />
            )}
            {task.isReset && (
              <MdIcons.MdCached
                size={24}
                color={theme.colors.isReset}
              />
            )}
            {task.isPullAhead && (
              <MdIcons.MdContentPasteGo
                size={24}
                color={theme.colors.info}
              />
            )}
            {task.isSplit && (
              <MdIcons.MdCallSplit
                size={24}
                color={theme.colors.info}
              />
            )}
            {task.isBreakIn && (
              <MdIcons.MdWarningAmber
                size={24}
                color={theme.colors.isBreakIn}
              />
            )}
          </Tile.Footer.Container>
        </Tile.Container>
      </Tile.Root>
    </Tile.Provider>
  )
})
